import { Button, Icon, Modal, Typography } from "@/components/atoms";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { getAssetUrl } from "@/utils";

interface FinishLaterModalProps {
  open: boolean;
  vehicle: Vehicle;
  currentStep: number;
  totalSteps?: number;
  onKeepGoing: () => void;
  onFinishLater: () => void;
}

/** How many steps a vehicle actually has now varies: a KYV-verified vehicle
 *  skips the verification step, so the count comes from the wizard rather than
 *  being fixed here — two places hardcoding 4 is how they drift apart. */
const DEFAULT_TOTAL_STEPS = 4;

export const FinishLaterModal = ({ open, vehicle, currentStep, totalSteps = DEFAULT_TOTAL_STEPS, onKeepGoing, onFinishLater }: FinishLaterModalProps) => {
  return (
    <Modal open={open} onClose={onKeepGoing} className="w-[85vw] !p-6 md:w-[450px] lg:w-[450px]">
      <div className="flex flex-col gap-5">
        <div className="rounded-lg border border-danger bg-danger/5 p-4">
          <div className="flex items-center gap-2">
            <Typography variant="action" className="text-danger">
              Vehicle can&apos;t be used yet
            </Typography>
            <Icon name="Danger" variant="Bold" size="sm" className="text-danger" />
          </div>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            {vehicle.model.make} {vehicle.model.model} won&apos;t appear in bookings or scheduling until all {totalSteps} steps are
            complete.
          </Typography>
        </div>

        <div className="flex items-center gap-3 rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-4 py-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-neutral-mid-gray">
            <img
              src={getAssetUrl(vehicle.model.avatar, { height: 56, width: 56, background: "transparent", fit: "contain" })}
              className="h-6 w-6"
              alt={`${vehicle.model.make} ${vehicle.model.model}`}
            />
          </div>
          <div className="flex flex-col">
            <Typography variant="action">
              {vehicle.model.make} {vehicle.model.model}{" "}
              <span className="font-normal text-neutral-dark-gray">{vehicle.registrationNumber}</span>
            </Typography>
            <div className="flex items-center gap-1.5 text-danger">
              <Icon name="InfoCircle" size="md" />
              <Typography variant="paragraph" className="text-danger">
                Incomplete — Step {currentStep} of {totalSteps}
              </Typography>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button variant="primary" size="lg" className="w-full" onClick={onKeepGoing}>
            Keep going, almost there
          </Button>
          <Button variant="secondary" size="lg" className="w-full" onClick={onFinishLater}>
            I&apos;ll finish it later
          </Button>
        </div>

        <Typography variant="paragraph" className="text-center text-neutral-silver-gray">
          Your progress is saved. You&apos;ll see a reminder to finish in the Vehicles list.
        </Typography>
      </div>
    </Modal>
  );
};
